import React, { type FC, useCallback, useMemo } from 'react';
import { type SxProps, TextField, type TextFieldProps } from '@mui/material';
import { type Theme, useTheme } from '@mui/material/styles';
import { mergeSx } from 'merge-sx';

import withFormControl from '@/hoc/withFormControl';
import { borders } from '@/theme/constants';
import shadows from '@/theme/shadows';

interface TextInputOwnProps {
  id?: string;
  name?: string;
  label?: string;
  placeholder?: string;
  value?: string | number;
  error?: string;
  onChange?: (event: string | number) => void;
  sx?: SxProps<Theme>;
  disabled?: boolean;
  numbersOnly?: boolean;
}

export type TextInputProps = Omit<TextFieldProps, 'error' | 'onChange'> & TextInputOwnProps;

const TextInputField: FC<TextInputProps> = ({
  id,
  label,
  placeholder,
  value,
  onChange,
  sx,
  error,
  InputProps,
  numbersOnly = false,
  disabled,
  ...props
}) => {
  const { palette } = useTheme();

  const isNumber = useMemo(() => InputProps?.type === 'number', [InputProps?.type]);

  const defaultValue = useMemo(() => (isNumber ? 0 : ''), [isNumber]);

  const onValueChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target?.value || defaultValue || '';
      const numericValue = newValue?.replace(/[^\d]/g, '');
      onChange?.(numbersOnly ? numericValue : newValue);
    },
    [defaultValue, numbersOnly, onChange]
  );

  return (
    <TextField
      {...props}
      id={id}
      placeholder={placeholder ?? label}
      value={value ?? defaultValue}
      onChange={onValueChange}
      inputProps={{ readOnly: disabled }}
      sx={mergeSx(
        {
          '& .MuiInputBase-root': {
            bgcolor: 'background.default',
            '&.Mui-focused': {
              boxShadow: shadows[2]
            },
            '& fieldset': {
              borderColor: 'grey.400'
            },
            ...(!!value && {
              '& fieldset': {
                borderColor: 'grey.600'
              }
            }),
            ...(!!error && {
              '&:hover fieldset': {
                borderColor: palette.error.main,
                borderWidth: borders._1px
              },
              '& fieldset': {
                borderWidth: borders._1px
              }
            })
          }
        },
        disabled && {
          '& .MuiInputBase-root': {
            bgcolor: 'grey.200',
            color: 'grey.500'
          }
        },
        sx
      )}
      label=""
      error={!!error}
      InputProps={InputProps}
    />
  );
};

const TextInput = withFormControl<string | number, string | number, TextInputProps>(TextInputField);

export default TextInput;
